"use client";

import { useEffect, Suspense } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { usePublicSession } from "@/contexts/PublicSessionContext";
import { Loader2 } from "lucide-react";
import { SignupForm } from "@/components/signup-form";
import {
  buildNewChatLaunchUrl,
  consumePendingChatLaunch,
} from "@/lib/pending-chat-launch";

interface AuthFlowProps {
  children: React.ReactNode;
}

// Routes that guests can access without authentication
const GUEST_ALLOWED_ROUTES = [
  "/",
  "/chat",
  "/replay",
  "/pricing",
  "/contact",
  "/trades",
  "/blog",
  // /alerts/{id} is intentionally public (shareable link). Signed-out
  // visitors still get gated by AlertPaywallContext's own AuthModal over
  // the DELAYED-view teaser — AuthFlow would otherwise stack its
  // SignupForm blur on top, producing the double-modal bug.
  "/alerts",
  // Library browsing is public; clicking into any card gates via the
  // in-page sign-in modal (LibraryClient). Changelog + feedback board are
  // fully public reads (posting/upvoting still requires auth server-side).
  "/library",
  "/updates",
  "/feedback",
  // Free public data pages (SEO + agent citation targets)
  "/flow",
  "/options-flow",
  "/screener",
  "/congressional-trading",
  "/congress",
  "/short-interest",
  "/gamma-exposure",
  "/max-pain",
  "/stocks",
];

// Helper function to check if a route is guest-accessible
function isGuestAllowedRoute(pathname: string | null): boolean {
  if (!pathname) return false;

  // Allow exact matches or routes that start with allowed paths
  return GUEST_ALLOWED_ROUTES.some((route) => {
    if (route === "/") return pathname === "/";
    return pathname === route || pathname.startsWith(`${route}/`);
  });
}

export function AuthFlow({ children }: AuthFlowProps) {
  const {
    user,
    loading: authLoading,
    emailVerified,
    checkingVerification,
  } = useAuth();
  const { isPublicSession, isCheckingSession } = usePublicSession();
  const router = useRouter();
  const pathname = usePathname();

  // Note: We no longer redirect to signup - instead we show blurred content with modal

  // Redirect to verify-email if authenticated but email not verified
  useEffect(() => {
    if (
      user &&
      emailVerified === false &&
      !checkingVerification &&
      pathname !== "/verify-email"
    ) {
      router.replace("/verify-email");
    }
  }, [user, emailVerified, checkingVerification, pathname, router]);

  useEffect(() => {
    if (!user || emailVerified !== true || checkingVerification) {
      return;
    }

    const pendingLaunch = consumePendingChatLaunch();
    if (!pendingLaunch?.prompt) {
      return;
    }

    router.push(buildNewChatLaunchUrl(pendingLaunch.prompt));
  }, [user, emailVerified, checkingVerification, router]);

  if (isGuestAllowedRoute(pathname)) {
    return <>{children}</>;
  }

  // Show loading while checking auth, session visibility, or verification status
  if (authLoading || isCheckingSession || checkingVerification) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Allow access if viewing a public session (even without authentication)
  if (isPublicSession) {
    return <>{children}</>;
  }

  // Not authenticated and not on allowed route - show blurred content with auth modal
  if (!user) {
    return (
      <div className="relative h-screen w-screen overflow-hidden">
        {/* Blurred background content */}
        <div className="blur-sm pointer-events-none select-none">
          {children}
        </div>
        {/* Auth modal overlay */}
        <div className="fixed inset-0 bg-background/40 flex items-center justify-center z-50 p-6">
          <div className="w-full max-w-sm bg-background rounded-lg border border-border p-6 shadow-lg">
            <Suspense
              fallback={
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
              }
            >
              <SignupForm />
            </Suspense>
          </div>
        </div>
      </div>
    );
  }

  // User authenticated but email not verified (and not on verify-email page) - show loading while redirecting
  if (emailVerified === false && pathname !== "/verify-email") {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <>{children}</>;
}
